import React from 'react';
import { Spinner } from 'react-bootstrap';
import { YSheet } from '../../../ui/sheet/YSheet';
import { YButton } from '../../../ui/YButton';
import { toolkitService } from '../services/toolkitService';
import api from '../../../utils/api';
import { stripPlanNameAcres } from './plantingPlansLibraryUtils';
import {
  SHARE_EMPTY,
  SHARE_LEDE,
  SHARE_LOAD_ERROR,
  SHARE_SEND_ERROR,
  SHARE_UNSHARE_ERROR,
  SHARE_WRONG_CLUB,
  canSendPlanToClub,
  formatShareApiError,
  isPlanSharedWithClub,
  presidentShareClubs,
  shareClubCta,
} from './plantingPlanShare';
import './plantingShareDialog.css';

export function PlantingShareDialog({
  group,
  show = false,
  onHide,
  onShared,
  onUnshared,
}) {
  const [clubs, setClubs] = React.useState([]);
  const [loadState, setLoadState] = React.useState('loading');
  const [busyId, setBusyId] = React.useState(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!show) return undefined;
    setBusyId(null);
    setError('');
    setClubs([]);
    setLoadState('loading');
    let cancelled = false;
    api.get('/clubs/')
      .then((response) => {
        if (cancelled) return;
        const list = Array.isArray(response?.data) ? response.data : [];
        setClubs(presidentShareClubs(list));
        setLoadState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setClubs([]);
        setLoadState('error');
      });
    return () => { cancelled = true; };
  }, [show, group?.id]);

  const handleClub = async (club) => {
    if (!group || busyId != null) return;
    const shared = isPlanSharedWithClub(group, club.id);
    if (!shared && !canSendPlanToClub(group, club.id)) return;
    setBusyId(club.id);
    setError('');
    try {
      const next = shared
        ? await toolkitService.unshareAreaGroup(group.id)
        : await toolkitService.shareAreaGroup(group.id, club.id);
      if (shared) onUnshared?.(next, club);
      else onShared?.(next, club);
    } catch (err) {
      setError(formatShareApiError(
        err,
        shared ? SHARE_UNSHARE_ERROR : SHARE_SEND_ERROR,
      ));
    } finally {
      setBusyId(null);
    }
  };

  const planName = stripPlanNameAcres(group?.name) || 'Untitled plan';

  return (
    <YSheet
      show={show}
      onHide={onHide}
      kicker="SHARE"
      title="Send to a club"
      testId="planting-plan-share-sheet"
      error={error || undefined}
      footer={(
        <YButton variant="ghost" onClick={onHide}>
          Cancel
        </YButton>
      )}
    >
      <p className="plant-share-kicker">{planName}</p>
      <p className="plant-share-lede">{SHARE_LEDE}</p>
      {loadState === 'loading' ? (
        <div data-testid="planting-plan-share-loading" className="plant-share-empty">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading clubs…
        </div>
      ) : null}
      {loadState === 'error' ? (
        <p data-testid="planting-plan-share-load-error" className="plant-share-load-error">
          {SHARE_LOAD_ERROR}
        </p>
      ) : null}
      {loadState === 'ready' && clubs.length === 0 ? (
        <p data-testid="planting-plan-share-empty" className="plant-share-empty">
          {SHARE_EMPTY}
        </p>
      ) : null}
      {loadState === 'ready'
        ? clubs.map((club) => {
          const shared = isPlanSharedWithClub(group, club.id);
          const blocked = !shared && !canSendPlanToClub(group, club.id);
          const busy = busyId === club.id;
          return (
            <button
              key={club.id}
              type="button"
              className={`plant-share-row${blocked ? ' is-blocked' : ''}`}
              data-testid={`planting-plan-share-club-${club.id}`}
              disabled={blocked || busyId != null}
              aria-label={
                blocked
                  ? `${club.name} unavailable. ${SHARE_WRONG_CLUB}`
                  : `${shareClubCta(shared)} ${club.name}`
              }
              onClick={() => { void handleClub(club); }}
            >
              <span className="plant-share-row__copy">
                <span className="plant-share-row__name">{club.name}</span>
                <span className="plant-share-row__sub">
                  {blocked
                    ? SHARE_WRONG_CLUB
                    : shared
                      ? 'Shared · read-only for members'
                      : 'Send a live read-only copy'}
                </span>
              </span>
              {busy ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <span
                  className={`plant-share-row__cta${
                    blocked ? ' plant-share-row__cta--blocked' : shared ? ' plant-share-row__cta--unshare' : ''
                  }`}
                >
                  {blocked ? '—' : shareClubCta(shared)}
                </span>
              )}
            </button>
          );
        })
        : null}
    </YSheet>
  );
}
